"use client";
import { cn } from "@/lib/utils/utils";
import { useAnimate } from "motion/react-mini";
import { useEffect, useRef } from "react";

export function Loader({
  className,
  state,
}: {
  className?: string;
  state: "pending" | "error" | "success";
}) {
  const [scope, animate] = useAnimate();
  const circleRef = useRef<ReturnType<typeof animate>>();
  const x1Ref = useRef<ReturnType<typeof animate>>();
  const x2Ref = useRef<ReturnType<typeof animate>>();
  const tickRef = useRef<ReturnType<typeof animate>>();

  useEffect(() => {
    circleRef.current?.stop();
    x1Ref.current?.stop();
    x2Ref.current?.stop();
    tickRef.current?.stop();
    if (state === "pending") {
      circleRef.current = animate(
        "#circle",
        {
          opacity: 1,
          strokeDashoffset: ["0.1", "0.9"],
          rotate: ["0deg", "360deg"],
        },
        {
          rotate: {
            duration: 0.8,
            ease: "linear",
            repeat: Infinity,
          },
          strokeDashoffset: {
            duration: 1,
            ease: "linear",
            repeat: Infinity,
            repeatType: "reverse",
          },
        }
      );

      x1Ref.current = animate(
        "#x1",
        {
          strokeDashoffset: "1",
        },
        { duration: 0.3 }
      );
      x2Ref.current = animate(
        "#x2",
        {
          strokeDashoffset: "1",
        },
        { duration: 0.3, delay: 0.1 }
      );
      tickRef.current = animate(
        "#tick",
        {
          strokeDashoffset: "1",
        },
        { duration: 0.3 }
      );
    }
    if (state === "error") {
      const currentRotate = parseFloat(
        (document.getElementById("circle")?.style.rotate ?? "0deg").replace(
          "deg",
          ""
        )
      );
      circleRef.current = animate(
        "#circle",
        {
          rotate: [`${currentRotate}deg`, `${currentRotate + 360}deg`],
          strokeDashoffset: "0",
        },
        {
          strokeDashoffset: { duration: 0.3, ease: "linear", delay: 0.5 },
          rotate: { duration: 0.5, ease: "linear", repeat: 2 },
        }
      );
      x1Ref.current = animate(
        "#x1",
        {
          strokeDashoffset: "0",
        },
        { duration: 0.3 }
      );
      x2Ref.current = animate(
        "#x2",
        {
          strokeDashoffset: "0",
        },
        { duration: 0.3, delay: 0.1 }
      );
      tickRef.current = animate(
        "#tick",
        {
          strokeDashoffset: "1",
        },
        { duration: 0.3 }
      );
    }
    if (state === "success") {
      circleRef.current = animate(
        "#circle",
        {
          strokeDashoffset: "0",
          rotate: "0deg",
        },
        { duration: 0.5 }
      );
      tickRef.current = animate(
        "#tick",
        {
          strokeDashoffset: "0",
        },
        { duration: 0.5, delay: 0.5 }
      );
      x1Ref.current = animate(
        "#x1",
        {
          strokeDashoffset: "1",
        },
        { duration: 0.3 }
      );
      x2Ref.current = animate(
        "#x2",
        {
          strokeDashoffset: "1",
        },
        { duration: 0.3, delay: 0.1 }
      );
    }
  }, [animate, state]);
  return (
    <div
      className={cn(
        "size-4 text-current transition-colors duration-300",
        className
      )}
    >
      <svg viewBox="0 0 50 50" ref={scope} className="loader">
        <path
          id="circle"
          fill="none"
          strokeWidth="3"
          strokeDasharray="1"
          pathLength="1"
          stroke="currentColor"
          style={{
            transformOrigin: "center",
          }}
          d="M 25, 5 a 20, 20 0 1,0 0,40 a 20, 20 0 1,0 0,-40"
        />
        <path
          id="tick"
          fill="none"
          strokeWidth="3"
          stroke="currentColor"
          d="M14,26 L 22,33 L 35,16"
          strokeDasharray="1 1"
          strokeDashoffset="1"
          pathLength="1"
        />
        <path
          id="x2"
          fill="none"
          strokeWidth="3"
          d="M17,17 L33,33"
          stroke="currentColor"
          strokeDasharray="1 1"
          strokeDashoffset="1"
          pathLength="1"
        />
        <path
          id="x1"
          stroke="currentColor"
          fill="none"
          strokeWidth="3"
          d="M33,17 L17,33"
          strokeDasharray="1 1"
          strokeDashoffset="1"
          pathLength="1"
        />
      </svg>
    </div>
  );
}
